/**
 * website-photo-zoom (Gabriel 2026-11-02): a service's, event's or booking's photo on the website, framed as the
 * studio set it in the crop tool, point and zoom, through the app's own FocalImage, so a studio's website and its
 * app show the same view. It fills the box it is placed in; the piece's box gives the size and the corners.
 */
import type { SitePhoto } from '@telify/shared';

import { FocalImage, type FocalImageProps } from '@/components/FocalImage';

import css from './Photo.module.css';

type PhotoProps = { readonly photo: SitePhoto } & Omit<
  FocalImageProps,
  'src' | 'alt' | 'focalPointX' | 'focalPointY' | 'focalPointScale' | 'children'
>;

export function Photo({ photo, className, ...rest }: PhotoProps) {
  return (
    <FocalImage
      {...rest}
      src={photo.url}
      alt=""
      focalPointX={photo.focalX}
      focalPointY={photo.focalY}
      focalPointScale={photo.focalScale ?? null}
      className={className ? `${css.photo} ${className}` : css.photo}
    />
  );
}

/** A booking's service photo and crop as the app's booking reads carry them, or null with no photo. */
export function bookingPhoto(service: {
  readonly imageUrl?: string | null;
  readonly focalPointX?: number | null;
  readonly focalPointY?: number | null;
  readonly focalPointScale?: number | null;
}): SitePhoto | null {
  return service.imageUrl
    ? { url: service.imageUrl, focalX: service.focalPointX ?? null, focalY: service.focalPointY ?? null, focalScale: service.focalPointScale ?? null }
    : null;
}
